import { useEffect, useState } from "react";
import {
    ActiveFilterChips, Button, FilterBar, FilterMenu, Input, SearchField, SegmentedControl, type AppliedChip, type FilterOption,
} from "@/shared/components/ds";
import type { LaunchListFilterOptions, LaunchListQuery, NamedOption } from "../models/launchList";
import styles from "./launchList.module.css";

interface Props {
    query: LaunchListQuery;
    options: LaunchListFilterOptions | null;
    /** How many launches the query matched, for the applied-filters lead. */
    count: number | null;
    /** Every change restarts at page 1; the page owns the URL. */
    onChange: (patch: Partial<LaunchListQuery>) => void;
}

const STATUS: FilterOption[] = [{ value: "1", label: "Enabled" }, { value: "0", label: "Paused" }, { value: "", label: "All" }];
const YES_NO: FilterOption[] = [{ value: "", label: "All" }, { value: "1", label: "Yes" }, { value: "0", label: "No" }];
const UNASSIGNED = "-2";

const DATE_BOUNDS: { key: keyof LaunchListQuery; label: string; chip: string }[] = [
    { key: "starts_after", label: "Starts On or After", chip: "Starts after" },
    { key: "starts_before", label: "Starts On or Before", chip: "Starts before" },
    { key: "ends_after", label: "Ends On or After", chip: "Ends after" },
    { key: "ends_before", label: "Ends On or Before", chip: "Ends before" },
];

/** Everything that is a filter, cleared at once. Sort, page size stay. */
export const NO_FILTERS: Partial<LaunchListQuery> = {
    status: "", affiliate: [], in_definition: "", has_control_group: "",
    starts_before: "", ends_before: "", starts_after: "", ends_after: "",
    engagement_manager: "", ops_manager: "", search: "",
    attribute: [], attribute_match: "any", created_by: [], modified_after: "",
};

const MATCH_LABELS = { any: "Match Any", all: "Match All" } as const;

const people = (list?: NamedOption[] | null): FilterOption[] => [
    { value: "", label: "All" },
    { value: UNASSIGNED, label: "Unassigned" },
    ...(list ?? []).map((p) => ({ value: String(p.id), label: p.name })),
];

const nameOf = (list: FilterOption[], value: string) => list.find((o) => o.value === value)?.label ?? value;

/**
 * The OP launch list filter bar (MPN-2229): what the old platform's /launches/ page offered,
 * as chips with popovers. The search waits a beat before it asks; everything else asks at
 * once. The date bounds, the attribute, creator and modified-after filters sit behind "More
 * filters" because they are rarely set.
 */
export function LaunchListFilters({ query, options, count, onChange }: Props) {
    const moreSet = DATE_BOUNDS.filter((b) => query[b.key] !== "").length
        + (query.attribute.length ? 1 : 0) + (query.created_by.length ? 1 : 0) + (query.modified_after ? 1 : 0);
    const [more, setMore] = useState(moreSet > 0);
    const [search, setSearch] = useState(query.search);

    // The URL is the truth: a chip removal or Clear all clears the box too.
    useEffect(() => { setSearch(query.search); }, [query.search]);
    useEffect(() => {
        if (search === query.search) return;
        const timer = setTimeout(() => onChange({ search }), 300);
        return () => clearTimeout(timer);
    }, [search]);

    const affiliates: FilterOption[] = (options?.affiliates ?? []).map((a) => ({ value: String(a.id), label: a.name }));
    const ems = people(options?.engagement_managers);
    const oms = people(options?.ops_managers);
    const attributes: FilterOption[] = (options?.launch_attributes ?? []).map((name) => ({ value: name, label: name }));
    const creators: FilterOption[] = (options?.creators ?? []).map((p) => ({ value: String(p.id), label: p.name }));

    const chips: AppliedChip[] = [];
    if (query.search) chips.push({ label: `Search: ${query.search}`, remove: () => onChange({ search: "" }) });
    query.affiliate.forEach((id) => chips.push({
        label: `Affiliate: ${nameOf(affiliates, id)}`, remove: () => onChange({ affiliate: query.affiliate.filter((a) => a !== id) }),
    }));
    if (query.status !== "") chips.push({ label: `Status at Shutdown: ${nameOf(STATUS, query.status)}`, remove: () => onChange({ status: "" }) });
    if (query.engagement_manager) chips.push({ label: `Engagement Manager: ${nameOf(ems, query.engagement_manager)}`, remove: () => onChange({ engagement_manager: "" }) });
    if (query.ops_manager) chips.push({ label: `Ops Manager: ${nameOf(oms, query.ops_manager)}`, remove: () => onChange({ ops_manager: "" }) });
    if (query.in_definition) chips.push({ label: `In Definition: ${nameOf(YES_NO, query.in_definition)}`, remove: () => onChange({ in_definition: "" }) });
    if (query.has_control_group) chips.push({ label: `Control Group: ${nameOf(YES_NO, query.has_control_group)}`, remove: () => onChange({ has_control_group: "" }) });
    if (query.attribute.length) chips.push({
        label: `${query.attribute_match === "all" && query.attribute.length > 1 ? "Attribute (all)" : "Attribute"}: ${query.attribute.join(", ")}`,
        remove: () => onChange({ attribute: [] }),
    });
    if (query.created_by.length) chips.push({
        label: `Created By: ${query.created_by.map((id) => nameOf(creators, id)).join(", ")}`, remove: () => onChange({ created_by: [] }),
    });
    DATE_BOUNDS.forEach((b) => {
        if (query[b.key]) chips.push({ label: `${b.chip} ${query[b.key]}`, remove: () => onChange({ [b.key]: "" }) });
    });
    if (query.modified_after) chips.push({ label: `Modified after ${query.modified_after}`, remove: () => onChange({ modified_after: "" }) });

    return (
        <>
            <div className={styles.bar}>
                <FilterBar>
                    <form onSubmit={(e) => { e.preventDefault(); onChange({ search }); }} style={{ margin: 0 }}>
                        <SearchField value={search} onChange={setSearch} onClear={() => { setSearch(""); onChange({ search: "" }); }}
                            placeholder="Search by launch name or ID, e.g. 14452" width={280} />
                    </form>
                    <FilterMenu kind="multi" label="Affiliate" options={affiliates} value={query.affiliate} searchable placeholder="e.g. Atlantic Cape CC"
                        onChange={(affiliate) => onChange({ affiliate })} />
                    <FilterMenu kind="single" label="Status at Shutdown" options={STATUS} value={query.status} allValue=""
                        onChange={(status) => onChange({ status })} />
                    <FilterMenu kind="single" label="Engagement Manager" options={ems} value={query.engagement_manager} allValue="" searchable placeholder="e.g. Jenna Morales"
                        onChange={(engagement_manager) => onChange({ engagement_manager })} />
                    <FilterMenu kind="single" label="Ops Manager" options={oms} value={query.ops_manager} allValue="" searchable placeholder="e.g. Luis Bautista"
                        onChange={(ops_manager) => onChange({ ops_manager })} />
                    <FilterMenu kind="single" label="In Definition" options={YES_NO} value={query.in_definition} allValue=""
                        onChange={(in_definition) => onChange({ in_definition })} />
                    <FilterMenu kind="single" label="Control Group" options={YES_NO} value={query.has_control_group} allValue=""
                        onChange={(has_control_group) => onChange({ has_control_group })} />
                    <div style={{ marginLeft: "auto" }}>
                        <Button variant={more ? "outline" : "secondary"} size="sm" icon={<i className="bi bi-sliders" aria-hidden="true" />}
                            aria-expanded={more} onClick={() => setMore(!more)}>
                            {moreSet ? `More filters · ${moreSet}` : "More filters"}
                        </Button>
                    </div>
                </FilterBar>
            </div>
            {more && (
                <div className={styles.morePanel}>
                    <div className={styles.moreField}>
                        <span className={styles.moreLabel}>Launch Attribute</span>
                        <FilterMenu kind="multi" label="Launch Attribute" options={attributes} value={query.attribute} searchable placeholder="e.g. personal_email"
                            popWidth={300} onChange={(attribute) => onChange({ attribute })}
                            header={
                                /* Match Any / Match All only matters for attributes, where one launch carries many. */
                                <SegmentedControl options={[MATCH_LABELS.any, MATCH_LABELS.all]} value={MATCH_LABELS[query.attribute_match]}
                                    onChange={(v) => onChange({ attribute_match: v === MATCH_LABELS.all ? "all" : "any" })} />
                            } />
                    </div>
                    <div className={styles.moreField}>
                        <span className={styles.moreLabel}>Created By</span>
                        <FilterMenu kind="multi" label="Created By" options={creators} value={query.created_by} searchable placeholder="e.g. Priya Shah"
                            onChange={(created_by) => onChange({ created_by })} />
                    </div>
                    {DATE_BOUNDS.map((b) => (
                        <Input key={b.key} label={b.label} type="date" value={query[b.key] as string}
                            onChange={(e) => onChange({ [b.key]: e.target.value })} />
                    ))}
                    <Input label="Modified On or After" type="date" value={query.modified_after}
                        onChange={(e) => onChange({ modified_after: e.target.value })} />
                </div>
            )}
            <ActiveFilterChips chips={chips} onClearAll={() => onChange(NO_FILTERS)} edge="bottom"
                leadLabel={count == null ? undefined : `${count.toLocaleString("en-US")} ${count === 1 ? "result" : "results"}`} />
        </>
    );
}
